/* eslint-disable @typescript-eslint/no-explicit-any */
import * as amplitude from '@amplitude/analytics-browser';
import { plugin as engagementPlugin } from '@amplitude/engagement-browser';
import { sessionReplayPlugin } from '@amplitude/plugin-session-replay-browser';
import { logger } from '../../helpers/logger';
import type { PrescriptionStartedPayload } from './types';
import type { PrescriptionCompletedPayload } from './types';
import type { ConcomitanceViewedPayload } from './types';
import type { ConcomitanceClickedPayload } from './types';
import type { RemovePrescriptionItemClickedPayload } from './types';
import type { EditPrescriptionItemClickedPayload } from './types';
import type { ProtocolTabClickedPayload } from './types';
import type { ProtocolCreatedPayload } from './types';
import type { ProtocolSelectedPayload } from './types';
import type { PopUpViewedPayload } from './types';
import type { PopUpClickedPayload } from './types';
import type { CompoundedMedicationCreatedPayload } from './types';
import type { CompoundedMedicationSavedAsPayload } from './types';
import type { CompoundedMedicationRemovedPayload } from './types';
import type { CompoundedMedicationClickedPayload } from './types';
import type { RenewedFromMedicalRecordPayload } from './types';
import type { ReissueFromMedicalRecordPayload } from './types';
import type { PrescriptionDraftSavedPayload } from './types';
import type { EsignatureSuccessPayload } from './types';
import type { EsignatureManagePayload } from './types';
import type { ManualMedicationClickedPayload } from './types';
import type { PrescriptionSentPayload } from './types';
import type { PatientProfileUpdatedPayload } from './types';
import type { DrugInteractionClickedPayload } from './types';
import type { WhatsNewClickedPayload } from './types';
import type { VoicePrescriptionClickedPayload } from './types';
import type { VoicePrescriptionRecordClickedPayload } from './types';
import type { VoicePrescriptionCancelledPayload } from './types';
import type { VoicePrescriptionConfirmedPayload } from './types';

export interface SessionReplayOptions {
  /**
   * Taxa de amostragem para gravação de sessões (0.0 a 1.0).
   * Ex: 0.01 grava 1% das sessões, 1.0 grava todas.
   * Default: 0.01
   */
  sampleRate: number;

  /**
   * Configurações de privacidade para mascaramento de dados sensíveis na gravação.
   */
  privacyConfig?: {
    /** Mascara o valor de todos os campos de input. */
    maskAllInputs?: boolean;
    /** Mascara todo o conteúdo de texto visível na tela. */
    maskAllText?: boolean;
    /** Bloqueia (oculta completamente) elementos que correspondam ao seletor CSS. */
    blockSelector?: string;
    /** Mascara elementos que correspondam ao seletor CSS, substituindo o conteúdo por um placeholder. */
    maskSelector?: string;
  };
}

export interface AmplitudeInitOptions {
  /**
   * Habilita o carregamento de configurações remotas definidas no dashboard do Amplitude.
   * Default: true
   */
  fetchRemoteConfig?: boolean;

  /**
   * Habilita a captura automática de eventos pelo SDK.
   * Controlado pela feature flag `Features.enableAmplitudeAutocapture`.
   * Default: false (desligado para evitar custos inesperados)
   */
  autocapture?: boolean;

  /**
   * Configuração do Session Replay para gravação de sessões de usuário.
   */
  sessionReplay?: SessionReplayOptions;
}

/** Tracking plan metadata. Valores que mudam por projeto ficam em env vars. */
const TRACKING_PLAN_CONFIG: amplitude.Types.BrowserOptions = {
  plan: {
    version: '2',
    branch: 'main',
    source: process.env.REACT_APP_AMPLITUDE_PLAN_SOURCE || '',
    versionId: process.env.REACT_APP_AMPLITUDE_PLAN_VERSION_ID || '',
  },
  ingestionMetadata: {
    sourceName: 'browser-typescript-ampli',
    sourceVersion: '2.0.0',
  },
};

const isDev = process.env.NODE_ENV === 'development';

/**
 * Wrapper sobre o Amplitude Browser SDK.
 * Substitui o ampli-cli gerado, centralizando inicialização, identify,
 * user properties e tracking de eventos.
 *
 * Nenhum evento é emitido e nenhum dado é salvo no localStorage
 * enquanto o módulo não for inicializado (controlado pela feature flag).
 */
class AmplitudeWrapper {
  private initialized = false;
  private client: amplitude.Types.BrowserClient | null = null;

  isInitialized(): boolean {
    return this.initialized;
  }

  /**
   * Inicializa o Amplitude SDK + Session Replay + Guides & Surveys.
   * Deve ser chamado uma única vez na raiz da aplicação.
   */
  init(apiKey: string, options?: AmplitudeInitOptions): void {
    if (this.initialized) return;

    if (!apiKey?.trim()) {
      if (isDev) logger.error('AmplitudeWrapper: API key inválida');
      return;
    }

    try {
      this.client = amplitude.createInstance();

      this.client.add(engagementPlugin());

      this.client.init(apiKey, undefined, {
        ...TRACKING_PLAN_CONFIG,
        fetchRemoteConfig: options?.fetchRemoteConfig ?? true,
        autocapture: options?.autocapture ?? false,
      } as any);

      if (options?.sessionReplay) {
        this.initSessionReplay(options.sessionReplay);
      }

      this.initialized = true;
    } catch (error) {
      this.initialized = false;
      this.client = null;
      if (isDev) logger.error('AmplitudeWrapper: Falha na inicialização', error);
    }
  }

  // ── Identify & User Properties ──────────────────────────────────

  /**
   * Identifica o usuário no Amplitude.
   * Seta o user_id na instância do SDK (necessário para segmentação de Guides & Surveys)
   * e dispara um identify event.
   * @param userId ID do usuário.
   */
  identify(userId: string): void {
    if (!this.initialized || !this.client) return;
    try {
      this.client.setUserId(userId);
      const identifyEvent = new amplitude.Identify();
      this.client.identify(identifyEvent);
    } catch (error) {
      if (isDev) logger.error('AmplitudeWrapper: Falha ao identificar usuário', error);
    }
  }

  /**
   * Seta uma única user property no Amplitude.
   * @param property Nome da propriedade.
   * @param value Valor da propriedade.
   */
  setUserProperty(property: string, value: any): void {
    if (!this.initialized || !this.client) return;
    try {
      const identifyEvent = new amplitude.Identify();
      identifyEvent.set(property, value);
      this.client.identify(identifyEvent);
    } catch (error) {
      if (isDev) logger.error('AmplitudeWrapper: Falha ao setar user property', error);
    }
  }

  /**
   * Seta múltiplas user properties de uma vez.
   * @param properties Objeto chave-valor com as propriedades.
   */
  setUserProperties(properties: Record<string, any>): void {
    if (!this.initialized || !this.client) return;
    try {
      const identifyEvent = new amplitude.Identify();
      for (const [key, value] of Object.entries(properties)) {
        identifyEvent.set(key, value);
      }
      this.client.identify(identifyEvent);
    } catch (error) {
      if (isDev) logger.error('AmplitudeWrapper: Falha ao setar user properties', error);
    }
  }

  // ── Event Tracking ──────────────────────────────────────────────

  /**
   * Trackeia um evento genérico no Amplitude.
   * @param eventName Nome do evento.
   * @param properties Propriedades do evento.
   */
  track(eventName: string, properties?: Record<string, any>): void {
    if (!this.initialized || !this.client) return;
    try {
      this.client.track(eventName, properties);
    } catch (error) {
      if (isDev) logger.error(`AmplitudeWrapper: Falha ao trackear evento "${eventName}"`, error);
    }
  }

  prescriptionStarted(properties: PrescriptionStartedPayload): void {
    this.track('prescription_started', properties);
  }

  prescriptionCompleted(properties: PrescriptionCompletedPayload): void {
    this.track('prescription_completed', properties as any);
  }

  concomitanceViewed(properties: ConcomitanceViewedPayload): void {
    this.track('concomitance_viewed', properties);
  }

  concomitanceClicked(properties: ConcomitanceClickedPayload): void {
    this.track('concomitance_clicked', properties);
  }

  removePrescriptionItemClicked(properties: RemovePrescriptionItemClickedPayload): void {
    this.track('remove_prescription_item_clicked', properties);
  }

  editPrescriptionItemClicked(properties: EditPrescriptionItemClickedPayload): void {
    this.track('edit_prescription_item_clicked', properties);
  }

  protocolTabClicked(properties: ProtocolTabClickedPayload): void {
    this.track('protocol_tab_clicked', properties);
  }

  protocolCreated(properties: ProtocolCreatedPayload): void {
    this.track('protocol_created', properties);
  }

  protocolSelected(properties: ProtocolSelectedPayload): void {
    this.track('protocol_selected', properties);
  }

  popUpViewed(properties: PopUpViewedPayload): void {
    this.track('pop_up_viewed', properties);
  }

  popUpClicked(properties: PopUpClickedPayload): void {
    this.track('pop_up_clicked', properties);
  }

  compoundedMedicationCreated(properties: CompoundedMedicationCreatedPayload): void {
    this.track('compounded_medication_created', properties);
  }

  compoundedMedicationSavedAs(properties: CompoundedMedicationSavedAsPayload): void {
    this.track('compounded_medication_saved_as', properties);
  }

  compoundedMedicationRemoved(properties: CompoundedMedicationRemovedPayload): void {
    this.track('compounded_medication_removed', properties);
  }

  compoundedMedicationClicked(properties: CompoundedMedicationClickedPayload): void {
    this.track('compounded_medication_clicked', properties);
  }

  renewedFromMedicalRecord(properties: RenewedFromMedicalRecordPayload): void {
    this.track('renewed_from_medical_record', properties);
  }

  reissueFromMedicalRecord(properties: ReissueFromMedicalRecordPayload): void {
    this.track('reissue_from_medical_record', properties);
  }

  prescriptionDraftSaved(properties: PrescriptionDraftSavedPayload): void {
    this.track('prescription_draft_saved', properties);
  }

  esignatureSuccess(properties: EsignatureSuccessPayload): void {
    this.track('esignature_sucess', properties);
  }

  esignatureManage(properties: EsignatureManagePayload): void {
    this.track('esignature_manage', properties);
  }

  manualMedicationClicked(properties: ManualMedicationClickedPayload): void {
    this.track('manual_medication_clicked', properties);
  }

  prescriptionSent(properties: PrescriptionSentPayload): void {
    this.track('prescription_sent', properties as any);
  }

  patientProfileUpdated(properties: PatientProfileUpdatedPayload): void {
    this.track('patient_profile_updated', properties as any);
  }

  drugInteractionClicked(properties: DrugInteractionClickedPayload): void {
    this.track('drug_interaction_clicked', properties as any);
  }

  whatsNewClicked(properties: WhatsNewClickedPayload): void {
    this.track('whats_new_clicked', properties as any);
  }

  voicePrescriptionClicked(properties: VoicePrescriptionClickedPayload): void {
    this.track('voice_prescription_clicked', properties);
  }

  voicePrescriptionRecordClicked(properties: VoicePrescriptionRecordClickedPayload): void {
    this.track('voice_prescription_record_clicked', properties);
  }

  voicePrescriptionCancelled(properties: VoicePrescriptionCancelledPayload): void {
    this.track('voice_prescription_cancelled', properties);
  }

  voicePrescriptionConfirmed(properties: VoicePrescriptionConfirmedPayload): void {
    this.track('voice_prescription_confirmed', properties);
  }

  /**
   * Força o envio imediato dos eventos em buffer.
   */
  flush(): void {
    if (!this.initialized || !this.client) return;
    this.client.flush();
  }

  // ── Session Replay ──────────────────────────────────────────────

  /**
   * Inicializa o plugin de Session Replay para gravação de sessões de usuário.
   * Chamado internamente pelo `init` quando `sessionReplay` é fornecido.
   */
  private initSessionReplay(options: SessionReplayOptions): void {
    if (!this.client) return;
    try {
      const plugin = sessionReplayPlugin({
        sampleRate: options.sampleRate ?? 0.01,
        ...options.privacyConfig,
      });
      this.client.add(plugin as any);
    } catch (error) {
      if (isDev) logger.error('AmplitudeWrapper: Falha ao inicializar Session Replay', error);
    }
  }
}

export const amplitudeModule = new AmplitudeWrapper();
